import { createContext, useContext, useState, type ReactNode } from 'react';
import {
  Box,
  FileText,
  ShoppingCart,
  Megaphone,
  MessageCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { useIsMobile } from '@/hooks/use-media-query';
import { useMobileSidebar } from './MobileSidebarContext';
import { ModelSelector } from '@/features/settings/model-selector';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { prefetchRoute } from '@/lib/performance/prefetch';
import { CAMPAIGNS_ENABLED } from '@/features/config/flags';
import { useRoles } from '@/hooks/use-roles';
import { ROLES, type Role } from '@/lib/user-roles';

const SIDEBAR_COLLAPSED_KEY = 'jenny-sidebar-collapsed';
const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 56;

interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue>({
  collapsed: false,
  setCollapsed: () => undefined,
});

export function useSidebarCollapsed() {
  return useContext(SidebarContext).collapsed;
}

export function useSidebarSetCollapsed() {
  return useContext(SidebarContext).setCollapsed;
}

export function SidebarStateProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useLocalStorage(SIDEBAR_COLLAPSED_KEY, false);
  const isMobile = useIsMobile();
  const effectiveCollapsed = isMobile ? false : collapsed;
  return (
    <SidebarContext.Provider value={{ collapsed: effectiveCollapsed, setCollapsed }}>
      {children}
    </SidebarContext.Provider>
  );
}

interface NavEntry {
  label: string;
  icon: LucideIcon;
  path: string;
  /** Hidden unless the user holds this role. */
  role?: Role;
  children?: {
    label: string;
    path?: string;
    children?: { label: string; path: string }[];
  }[];
}

const NAV_ENTRIES: NavEntry[] = [
  { label: 'Products', icon: Box, path: ROUTES.PRODUCTS },
  { label: 'Free Text', icon: FileText, path: ROUTES.FREE_TEXT },
  { label: 'eCommerce', icon: ShoppingCart, path: ROUTES.ECOMMERCE },
  ...(CAMPAIGNS_ENABLED
    ? [
        {
          label: 'Campaigns',
          icon: Megaphone,
          path: ROUTES.CAMPAIGNS,
          role: ROLES.CAMPAIGNS,
          children: [
            { label: 'My Campaigns', path: ROUTES.CAMPAIGNS },
            {
              label: 'Templates',
              children: [{ label: 'Newsletter', path: ROUTES.NEWSLETTER_TEMPLATES }],
            },
          ],
        },
      ]
    : []),
  { label: 'Ask Jenny', icon: MessageCircle, path: ROUTES.CHATBOT },
];

interface SidebarProps {
  nav?: ReactNode;
  bottom?: ReactNode;
}

export function Sidebar({ nav, bottom }: SidebarProps) {
  const isMobile = useIsMobile();
  const { isOpen, close } = useMobileSidebar();
  const effectiveCollapsed = useSidebarCollapsed();
  const setCollapsed = useSidebarSetCollapsed();
  const location = useLocation();
  const { roles } = useRoles();
  const navEntries = NAV_ENTRIES.filter((entry) => !entry.role || roles.includes(entry.role));
  const [campaignsOpen, setCampaignsOpen] = useState(() =>
    location.pathname.startsWith(ROUTES.CAMPAIGNS),
  );
  const [templatesOpen, setTemplatesOpen] = useState(() =>
    location.pathname.startsWith(ROUTES.NEWSLETTER_TEMPLATES),
  );

  return (
    <>
      <TooltipProvider>
        {isMobile && isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 transition-opacity"
            onClick={close}
            aria-hidden="true"
          />
        )}

        <aside
          data-surface="contrast"
          data-testid="sidebar"
          className={cn(
            'flex shrink-0 flex-col overflow-hidden bg-[var(--bg-primary)] text-[var(--text-primary)] transition-all duration-200 ease-in-out',
            isMobile ? 'fixed top-14 bottom-0 left-0 z-50 shadow-lg' : 'relative',
            isMobile && !isOpen && '-translate-x-full',
            isMobile && isOpen && 'translate-x-0',
          )}
          style={{
            width: isMobile
              ? EXPANDED_WIDTH
              : effectiveCollapsed
                ? COLLAPSED_WIDTH
                : EXPANDED_WIDTH,
          }}
        >
          {!isMobile && (
            <div
              className={cn(
                'flex h-11 items-center border-b border-white/10',
                effectiveCollapsed ? 'justify-center' : 'gap-3 px-4',
              )}
            >
              <button
                type="button"
                onClick={() => {
                  setCollapsed(!effectiveCollapsed);
                }}
                aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded border-none bg-transparent p-0 text-inherit hover:bg-white/10 transition-colors"
              >
                {effectiveCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              </button>
              {!effectiveCollapsed && <span className="whitespace-nowrap font-bold">Menu</span>}
            </div>
          )}

          {isMobile && (
            <div className="flex h-11 items-center gap-3 px-4 border-b border-white/10">
              <span className="whitespace-nowrap font-bold">Menu</span>
            </div>
          )}

          <nav aria-label="Main navigation" className="flex-1 overflow-hidden">
            {nav ?? (
              <ul
                className={cn(
                  'm-0 list-none p-0',
                  effectiveCollapsed && 'flex flex-col items-center gap-3 py-3',
                )}
              >
                {navEntries.map((entry) => {
                  const Icon = entry.icon;

                  if (entry.children && !effectiveCollapsed) {
                    const active = location.pathname.startsWith(entry.path);
                    return (
                      <li key={entry.path}>
                        <button
                          type="button"
                          aria-expanded={campaignsOpen}
                          onClick={() => {
                            setCampaignsOpen((open) => !open);
                          }}
                          className={cn(
                            'flex h-11 w-full items-center gap-3 border-0 border-l-[3px] bg-transparent px-4 text-left text-[length:var(--text-sm)] text-inherit transition-colors hover:bg-white/10',
                            active
                              ? 'border-l-[var(--accent-pure)] bg-white/8 pl-[13px] font-semibold'
                              : 'border-l-transparent',
                          )}
                        >
                          <Icon size={20} className="shrink-0" />
                          <span className="flex-1">{entry.label}</span>
                          {campaignsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                        {campaignsOpen && (
                          <ul className="m-0 list-none bg-black/10 py-1">
                            {entry.children.map((child) => {
                              if (child.path) {
                                return (
                                  <li key={child.path}>
                                    <NavLink
                                      to={child.path}
                                      end={child.path === ROUTES.CAMPAIGNS}
                                      onClick={isMobile ? close : undefined}
                                      className={({ isActive }) =>
                                        cn(
                                          'flex h-9 items-center border-l-[3px] pl-10 text-[length:var(--text-sm)] text-inherit no-underline hover:bg-white/10',
                                          isActive
                                            ? 'border-l-[var(--accent-pure)] bg-white/8 font-semibold'
                                            : 'border-l-transparent',
                                        )
                                      }
                                    >
                                      {child.label}
                                    </NavLink>
                                  </li>
                                );
                              }

                              return (
                                <li key={child.label}>
                                  <button
                                    type="button"
                                    aria-expanded={templatesOpen}
                                    onClick={() => {
                                      setTemplatesOpen((open) => !open);
                                    }}
                                    className={cn(
                                      'flex h-9 w-full items-center border-0 border-l-[3px] border-l-transparent bg-transparent pr-4 pl-10 text-left text-[length:var(--text-sm)] text-inherit hover:bg-white/10',
                                      child.children?.some((nested) =>
                                        location.pathname.startsWith(nested.path),
                                      ) && 'font-semibold',
                                    )}
                                  >
                                    <span className="flex-1">{child.label}</span>
                                    {templatesOpen ? (
                                      <ChevronDown size={15} />
                                    ) : (
                                      <ChevronRight size={15} />
                                    )}
                                  </button>
                                  {templatesOpen && child.children && (
                                    <ul className="m-0 list-none bg-black/10 py-1">
                                      {child.children.map((templateChild) => (
                                        <li key={templateChild.path}>
                                          <NavLink
                                            to={templateChild.path}
                                            onClick={isMobile ? close : undefined}
                                            className={({ isActive }) =>
                                              cn(
                                                'flex h-9 items-center border-l-[3px] pl-14 text-[length:var(--text-sm)] text-inherit no-underline hover:bg-white/10',
                                                isActive
                                                  ? 'border-l-[var(--accent-pure)] bg-white/8 font-semibold'
                                                  : 'border-l-transparent',
                                              )
                                            }
                                          >
                                            {templateChild.label}
                                          </NavLink>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  }

                  const link = effectiveCollapsed ? (
                    <NavLink
                      to={entry.path}
                      end={false}
                      onMouseEnter={() => {
                        prefetchRoute(entry.path);
                      }}
                      onFocus={() => {
                        prefetchRoute(entry.path);
                      }}
                      className={({ isActive }) =>
                        cn(
                          'flex h-10 w-10 items-center justify-center rounded-lg mx-auto text-inherit no-underline transition-colors',
                          'hover:bg-white/10',
                          isActive && 'bg-white/12 text-[var(--accent-pure)]',
                        )
                      }
                    >
                      <Icon size={20} className="shrink-0" />
                    </NavLink>
                  ) : (
                    <NavLink
                      to={entry.path}
                      end={false}
                      onClick={isMobile ? close : undefined}
                      onMouseEnter={() => {
                        prefetchRoute(entry.path);
                      }}
                      onFocus={() => {
                        prefetchRoute(entry.path);
                      }}
                      className={({ isActive }) =>
                        cn(
                          'flex h-11 items-center gap-3 whitespace-nowrap px-4 text-[length:var(--text-sm)] text-inherit no-underline transition-colors',
                          'hover:bg-white/10',
                          isActive
                            ? 'border-l-[3px] border-l-[var(--accent-pure)] bg-white/8 pl-[13px] font-semibold'
                            : 'border-l-[3px] border-l-transparent',
                        )
                      }
                    >
                      <Icon size={20} className="shrink-0" />
                      {entry.label}
                    </NavLink>
                  );

                  if (effectiveCollapsed) {
                    return (
                      <li key={entry.path}>
                        <Tooltip>
                          <TooltipTrigger asChild>{link}</TooltipTrigger>
                          <TooltipContent side="right">{entry.label}</TooltipContent>
                        </Tooltip>
                      </li>
                    );
                  }

                  return <li key={entry.path}>{link}</li>;
                })}
              </ul>
            )}
          </nav>

          {!effectiveCollapsed && <div className="p-4">{bottom ?? <ModelSelector />}</div>}
        </aside>
      </TooltipProvider>
    </>
  );
}
